import {
  Alert,
  Button,
  DatePicker,
  FormField,
  Input,
  NumberStepper,
  Select,
  Text,
  Textarea,
  WeightInput,
  todayIso,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { formErrors } from '../../../lib/errors';
import { useColonies, useLocations } from '../../apiary/api';
import { ColonyPicker } from '../../apiary/components/ColonyPicker';
import { useSaveBatch, useVarieties, type Batch, type SaveBatchRequest } from '../api';
import { defaultBestBefore, formatKg, splitEvenly } from '../format';
import styles from './harvest.module.css';

export interface BatchFormDialogProps {
  open: boolean;
  onClose: () => void;
  batch?: Batch;
  onSaved?: (batch: Batch) => void;
}

/** Manual shares (grams) per colony; `null` = share of the rest. */
type Manual = Record<string, number | null>;

/** Distributes like the server (HRV-003): manual shares stay, the rest is split evenly. */
export function distribute(total: number, selected: string[], manual: Manual) {
  const fixed = selected.reduce((sum, id) => sum + (manual[id] ?? 0), 0);
  const open = selected.filter((id) => manual[id] == null);
  const rest = Math.max(0, total - fixed);
  const even = splitEvenly(rest, open.length);
  const shares = new Map<string, number>();
  selected.forEach((id) => {
    const m = manual[id];
    shares.set(id, m != null ? m : (even[open.indexOf(id)] ?? 0));
  });
  return { shares, fixed, open: open.length, rest };
}

function initialManual(batch?: Batch): Manual {
  if (!batch || batch.shares.length === 0) return {};
  const grams = batch.shares.map((s) => s.grams);
  const even = Math.max(...grams) - Math.min(...grams) <= 1;
  return Object.fromEntries(batch.shares.map((s) => [s.colony.id, even ? null : s.grams]));
}

/** Records a harvest as batch with yield shares (HRV-001 – HRV-003). */
export function BatchFormDialog({ open, onClose, batch, onSaved }: BatchFormDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const varieties = useVarieties(false, open);
  const locations = useLocations(false, open);
  const colonies = useColonies({ status: batch ? 'all' : 'active' }, open);
  const save = useSaveBatch();
  const [harvestedOn, setHarvestedOn] = useState(batch?.harvested_on ?? todayIso());
  const [variety, setVariety] = useState(batch?.variety.id ?? '');
  const [weight, setWeight] = useState<number | null>(batch?.weight_grams ?? null);
  const [location, setLocation] = useState(batch?.location?.id ?? '');
  const [selected, setSelected] = useState<string[]>(batch?.shares.map((s) => s.colony.id) ?? []);
  const [manual, setManual] = useState<Manual>(() => initialManual(batch));
  const [water, setWater] = useState<number | null>(batch?.water_content ?? null);
  const [bestBefore, setBestBefore] = useState<string | null>(batch?.best_before ?? null);
  const [lot, setLot] = useState('');
  const [notes, setNotes] = useState(batch?.notes ?? '');
  const varietyValue = variety || varieties.data?.[0]?.id || '';
  const total = weight ?? 0;
  const { shares, fixed, open: openCount } = distribute(total, selected, manual);
  const names = new Map((colonies.data ?? []).map((c) => [c.id, c.name]));
  batch?.shares.forEach((s) => names.set(s.colony.id, s.colony.name));
  const errors = formErrors(save.error, {
    weight: ['batch.weight'],
    variety: ['variety.'],
    harvestedOn: ['batch.harvested'],
    bestBefore: ['batch.best_before'],
    water: ['water_content.'],
    lot: ['lot', 'lot_number.'],
    colonies: ['yield.', 'colony.', 'colonies.'],
  });

  const close = () => {
    save.reset();
    onClose();
  };

  /** HRV-002: choosing a location proposes all its active colonies. */
  const chooseLocation = (id: string) => {
    setLocation(id);
    if (id && selected.length === 0) {
      setSelected(
        (colonies.data ?? [])
          .filter((c) => c.status === 'active' && c.location.id === id)
          .map((c) => c.id),
      );
    }
  };

  /** The location follows the colonies if they all stand at one place. */
  const chooseColonies = (ids: string[]) => {
    setSelected(ids);
    const places = new Set(
      (colonies.data ?? []).filter((c) => ids.includes(c.id)).map((c) => c.location.id),
    );
    if (places.size === 1) setLocation([...places][0] ?? '');
    else if (places.size > 1) setLocation('');
  };

  const submit = () => {
    const body: SaveBatchRequest = {
      harvested_on: harvestedOn,
      variety_id: varietyValue,
      weight_grams: weight ?? 0,
      location_id: location || null,
      water_content: water,
      best_before: bestBefore ?? defaultBestBefore(harvestedOn),
      notes: notes || null,
      colonies: selected.map((id) => ({ colony_id: id, grams: manual[id] ?? null })),
    };
    save.mutate(
      { id: batch?.id, body, lot: lot.trim() || undefined, version: batch?.version },
      {
        onSuccess: (saved) => {
          toast.show(
            batch ? t('harvest.batch.saved') : t('harvest.batch.recorded', { lot: saved.lot }),
          );
          onSaved?.(saved);
          close();
        },
      },
    );
  };

  const overbooked = fixed > total;
  const mixed = selected.length > 0 && !location;

  return (
    <FormDialog
      open={open}
      onClose={close}
      drawer
      size="lg"
      title={batch ? t('harvest.batch.editTitle', { lot: batch.lot }) : t('harvest.batch.newTitle')}
      submitLabel={t('common.save')}
      onSubmit={submit}
      pending={save.isPending}
      error={errors.general}
    >
      <div className={styles.row}>
        <FormField label={t('harvest.batch.harvestedOn')} required error={errors.field.harvestedOn}>
          {(p) => (
            <DatePicker {...p} value={harvestedOn} onChange={setHarvestedOn} max={todayIso()} />
          )}
        </FormField>
        <FormField
          label={t('harvest.batch.variety')}
          required
          error={errors.field.variety}
          hint={batch?.has_stock_activity ? t('harvest.batch.varietyLocked') : undefined}
        >
          {(p) => (
            <Select
              {...p}
              value={varietyValue}
              onChange={(e) => setVariety(e.target.value)}
              disabled={batch?.has_stock_activity}
              options={(varieties.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
            />
          )}
        </FormField>
      </div>
      <div className={styles.row}>
        <FormField label={t('harvest.batch.weight')} required error={errors.field.weight}>
          {(p) => (
            <WeightInput
              {...p}
              value={weight}
              onChange={setWeight}
              labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
            />
          )}
        </FormField>
        <FormField label={t('harvest.batch.water')} error={errors.field.water}>
          {(p) => (
            <NumberStepper
              {...p}
              value={water}
              onChange={setWater}
              min={10}
              max={30}
              step={0.1}
              decimals={1}
              unit="%"
              labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
            />
          )}
        </FormField>
      </div>
      {water !== null && water > 18 && (
        <Alert tone="warning" title={t('harvest.water.warningTitle')}>
          {t('harvest.water.warning')}
        </Alert>
      )}

      <FormField
        label={t('harvest.batch.location')}
        hint={mixed ? t('harvest.batch.mixed') : undefined}
      >
        {(p) => (
          <Select
            {...p}
            value={location}
            onChange={(e) => chooseLocation(e.target.value)}
            options={[
              { value: '', label: t('harvest.batch.noLocation') },
              ...(locations.data ?? []).map((l) => ({ value: l.id, label: l.name })),
            ]}
          />
        )}
      </FormField>

      <ColonyPicker
        colonies={colonies.data ?? []}
        selected={selected}
        onChange={chooseColonies}
        error={errors.field.colonies}
        locationId={location}
      />

      {selected.length > 0 ? (
        <fieldset className={styles.shares}>
          <legend>{t('harvest.shares.title')}</legend>
          <Text size="sm" tone="muted">
            {t('harvest.shares.help')}
          </Text>
          <ul className={styles.shareList}>
            {selected.map((id) => (
              <li key={id} className={styles.shareRow}>
                <span className={styles.shareName}>{names.get(id) ?? '…'}</span>
                <WeightInput
                  aria-label={t('harvest.shares.amountFor', { colony: names.get(id) ?? '' })}
                  size="sm"
                  value={manual[id] ?? null}
                  placeholder={formatKg(shares.get(id) ?? 0, 3).replace(' kg', '')}
                  onChange={(g) => setManual((m) => ({ ...m, [id]: g }))}
                  labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
                />
                <Text as="span" size="xs" tone={manual[id] != null ? 'default' : 'muted'}>
                  {manual[id] != null ? t('harvest.shares.manual') : t('harvest.shares.auto')}
                </Text>
              </li>
            ))}
          </ul>
          <div className={styles.shareFooter}>
            <Text
              size="sm"
              tone={overbooked ? 'danger' : 'muted'}
              role={overbooked ? 'alert' : undefined}
            >
              {overbooked
                ? t('harvest.shares.overbooked', { fixed: formatKg(fixed), total: formatKg(total) })
                : openCount === 0 && fixed !== total
                  ? t('harvest.shares.mismatch', { fixed: formatKg(fixed), total: formatKg(total) })
                  : t('harvest.shares.sum', { total: formatKg(total) })}
            </Text>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setManual({})}
              disabled={Object.values(manual).every((v) => v == null)}
            >
              {t('harvest.shares.even')}
            </Button>
          </div>
        </fieldset>
      ) : (
        <Text size="sm" tone="muted">
          {t('harvest.shares.none')}
        </Text>
      )}

      <div className={styles.row}>
        <FormField label={t('harvest.batch.bestBefore')} error={errors.field.bestBefore}>
          {(p) => (
            <DatePicker
              {...p}
              value={bestBefore ?? defaultBestBefore(harvestedOn)}
              onChange={setBestBefore}
            />
          )}
        </FormField>
        {!batch && (
          <FormField
            label={t('harvest.batch.lot')}
            hint={t('harvest.batch.lotHint')}
            error={errors.field.lot}
          >
            {(p) => <Input {...p} value={lot} onChange={(e) => setLot(e.target.value)} />}
          </FormField>
        )}
      </div>
      <FormField label={t('harvest.batch.notes')}>
        {(p) => (
          <Textarea {...p} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        )}
      </FormField>
    </FormDialog>
  );
}
